/**
 * Calculate occupancy percentage for a time slot
 */
export const calculateOccupancyPercentage = (
    usedCapacity: number,
    totalCapacity: number
  ): number => {
    if (totalCapacity === 0) return 0;
    return Math.round((usedCapacity / totalCapacity) * 100);
  };
  
  /**
   * Get color based on occupancy percentage
   */
  export const getOccupancyColor = (percentage: number): string => {
    if (percentage === 0) return '#28a745'; // Green - Available
    if (percentage <= 60) return '#ffc107'; // Yellow - Partially booked
    if (percentage < 100) return '#ff9800'; // Orange - Almost full
    return '#dc3545'; // Red - Fully booked
  };
  
  /**
   * Get occupancy label
   */
  export const getOccupancyLabel = (percentage: number, t: any): string => {
    if (percentage === 0) return t('capacity.available');
    if (percentage <= 60) return t('capacity.partiallyBooked');
    if (percentage < 100) return t('capacity.almostFull');
    return t('capacity.fullyBooked');
  };
  
  /**
   * Get occupancy icon
   */
  export const getOccupancyIcon = (percentage: number): string => {
    if (percentage === 0) return 'check circle';
    if (percentage <= 60) return 'circle notched';
    if (percentage < 100) return 'exclamation circle';
    return 'ban';
  };
  
  /**
   * Format capacity display (e.g., "3/6 units")
   */
  export const formatCapacityDisplay = (
    usedCapacity: number,
    totalCapacity: number,
    t: any
  ): string => {
    return `${usedCapacity}/${totalCapacity} ${t('capacity.units')}`;
  };
  
  /**
   * Check if time slots overlap
   */
  export const doTimeSlotsOverlap = (
    start1: Date,
    end1: Date,
    start2: Date,
    end2: Date
  ): boolean => {
    return start1 < end2 && end1 > start2;
  };
  
  /**
   * Aggregate bookings by time slot
   */
  export interface AggregatedTimeSlot {
    startTime: Date;
    endTime: Date;
    totalCapacity: number;
    usedCapacity: number;
    occupancyPercentage: number;
    bookingIds: string[];
  }
  
  export const aggregateBookingsByTimeSlot = (
    bookings: Array<{
      id: string;
      start: Date;
      end: Date;
      capacityUsed: number;
      resourceId: string;
    }>,
    resourceId: string,
    totalCapacity: number
  ): AggregatedTimeSlot[] => {
    const resourceBookings = bookings.filter(b => b.resourceId === resourceId);
    
    if (resourceBookings.length === 0) {
      return [];
    }
  
    // Group overlapping bookings
    const timeSlots: AggregatedTimeSlot[] = [];
  
    resourceBookings.forEach(booking => {
      // Find existing slot that overlaps
      let existingSlot = timeSlots.find(slot =>
        doTimeSlotsOverlap(
          slot.startTime,
          slot.endTime,
          new Date(booking.start),
          new Date(booking.end)
        )
      );
  
      if (existingSlot) {
        // Update existing slot
        existingSlot.startTime = new Date(
          Math.min(existingSlot.startTime.getTime(), new Date(booking.start).getTime())
        );
        existingSlot.endTime = new Date(
          Math.max(existingSlot.endTime.getTime(), new Date(booking.end).getTime())
        );
        existingSlot.usedCapacity += booking.capacityUsed;
        existingSlot.occupancyPercentage = calculateOccupancyPercentage(
          existingSlot.usedCapacity,
          totalCapacity
        );
        existingSlot.bookingIds.push(booking.id);
      } else {
        // Create new slot
        const usedCapacity = booking.capacityUsed;
        timeSlots.push({
          startTime: new Date(booking.start),
          endTime: new Date(booking.end),
          totalCapacity,
          usedCapacity,
          occupancyPercentage: calculateOccupancyPercentage(usedCapacity, totalCapacity),
          bookingIds: [booking.id]
        });
      }
    });
  
    return timeSlots;
  };